import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  api,
  deviceUuid,
  storeToken,
  storedToken,
  type CurrentUser,
  type OwnedNumber,
  type Session
} from "./api";
import { DeviceSocket, type ServerEvent } from "./socket";

/** A ringing or in-progress call, as the call screens need it. */
export interface LiveCall {
  callSessionId: string;
  numberId: string;
  number: string;
  counterparty: string;
  counterpartyLabel: string;
  state: "ringing" | "in_progress";
  startedAt: number;
}

interface AppState {
  user: CurrentUser | null;
  numbers: OwnedNumber[];
  loading: boolean;
  /** Bumped on every server event, so views can refetch without threading props. */
  revision: number;
  liveCall: LiveCall | null;
  callEnded: string | null;
  login(email: string, password: string): Promise<void>;
  register(email: string, password: string, displayName: string): Promise<void>;
  /** Claim the admin role, if this account is the one the deployment allows. Throws on refusal. */
  promoteToAdmin(): Promise<void>;
  logout(): Promise<void>;
  refreshNumbers(): Promise<void>;
  answerCall(callSessionId: string): Promise<void>;
  declineCall(callSessionId: string): Promise<void>;
  hangUpCall(callSessionId: string): Promise<void>;
  dismissEndedCall(): void;
  onEvent(listener: (event: ServerEvent) => void): () => void;
}

const AppContext = createContext<AppState | null>(null);

export function useApp(): AppState {
  const state = useContext(AppContext);
  if (!state) {
    throw new Error("useApp must be used inside AppProvider");
  }
  return state;
}

export function AppProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [numbers, setNumbers] = useState<OwnedNumber[]>([]);
  const [loading, setLoading] = useState(true);
  const [revision, setRevision] = useState(0);
  const [liveCall, setLiveCall] = useState<LiveCall | null>(null);
  const [callEnded, setCallEnded] = useState<string | null>(null);
  const socketRef = useRef<DeviceSocket | null>(null);
  const listenersRef = useRef(new Set<(event: ServerEvent) => void>());

  const refreshNumbers = useCallback(async () => {
    const owned = await api.get<OwnedNumber[]>("/api/v1/numbers");
    setNumbers(owned);
    return owned;
  }, []);

  /**
   * Registers this browser as a device for every active number, then opens the socket.
   * Registration has to happen first: the backend refuses a socket for an unregistered device, because
   * the fan-out set is the registered set.
   */
  const startRealtime = useCallback(
    async (token: string) => {
      const owned = await refreshNumbers();
      const activeNumberIds = owned.filter((number) => number.state === "ACTIVE").map((number) => number.id);
      try {
        await api.post("/api/v1/devices", {
          deviceUuid: deviceUuid(),
          displayName: "Web client",
          platform: "WEB",
          numberIds: activeNumberIds
        });
      } catch {
        // A full device slot must not block the rest of the app; the user just will not ring here.
      }
      socketRef.current?.close();
      const socket = new DeviceSocket(token, deviceUuid());
      socket.onEvent((event) => {
        listenersRef.current.forEach((listener) => listener(event));
        handleEvent(event);
      });
      socket.connect();
      socketRef.current = socket;
    },
    [refreshNumbers]
  );

  const handleEvent = useCallback((event: ServerEvent) => {
    setRevision((current) => current + 1);
    const data = event.data as Record<string, string>;
    switch (event.type) {
      case "call.incoming":
        setLiveCall({
          callSessionId: data.callSessionId,
          numberId: data.numberId,
          number: data.number,
          counterparty: data.counterparty,
          counterpartyLabel: data.counterpartyLabel ?? data.counterparty,
          state: "ringing",
          startedAt: Date.now()
        });
        break;
      case "call.answered":
        setLiveCall((current) =>
          current && current.callSessionId === data.callSessionId
            ? { ...current, state: "in_progress", startedAt: Date.now() }
            : current
        );
        break;
      case "call.cancelled":
        setLiveCall((current) => (current?.callSessionId === data.callSessionId ? null : current));
        setCallEnded(data.reason ?? "cancelled");
        break;
      case "call.ended":
        setLiveCall((current) => (current?.callSessionId === data.callSessionId ? null : current));
        setCallEnded(data.callStatus ?? "ended");
        break;
      default:
        break;
    }
  }, []);

  useEffect(() => {
    const token = storedToken();
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get<CurrentUser>("/api/v1/auth/me")
      .then(async (me) => {
        setUser(me);
        await startRealtime(token);
      })
      .catch(() => storeToken(null))
      .finally(() => setLoading(false));
  }, [startRealtime]);

  const afterSession = useCallback(
    async (session: Session) => {
      storeToken(session.token);
      setUser(session.user);
      await startRealtime(session.token);
    },
    [startRealtime]
  );

  const state: AppState = useMemo(
    () => ({
      user,
      numbers,
      loading,
      revision,
      liveCall,
      callEnded,
      async login(email, password) {
        await afterSession(await api.post<Session>("/api/v1/auth/login", { email, password }));
      },
      async register(email, password, displayName) {
        await afterSession(
          await api.post<Session>("/api/v1/auth/register", { email, password, displayName })
        );
      },
      async promoteToAdmin() {
        // The role lives on the account, and the identity is resolved from the database on every request,
        // so the existing token becomes an admin token without signing in again. The returned user is set
        // so the menu and the admin guard update immediately.
        const updated = await api.post<CurrentUser>("/api/v1/auth/promote-to-admin", {});
        setUser(updated);
      },
      async logout() {
        try {
          await api.post("/api/v1/auth/logout");
        } finally {
          socketRef.current?.close();
          storeToken(null);
          setUser(null);
          setNumbers([]);
        }
      },
      refreshNumbers: async () => {
        await refreshNumbers();
      },
      async answerCall(callSessionId) {
        await api.post(`/api/v1/call-sessions/${callSessionId}/answer`, { deviceUuid: deviceUuid() });
        setLiveCall((current) =>
          current ? { ...current, state: "in_progress", startedAt: Date.now() } : current
        );
      },
      async declineCall(callSessionId) {
        await api.post(`/api/v1/call-sessions/${callSessionId}/decline`, { deviceUuid: deviceUuid() });
        setLiveCall(null);
      },
      async hangUpCall(callSessionId) {
        await api.post(`/api/v1/call-sessions/${callSessionId}/hangup`);
        setLiveCall(null);
      },
      dismissEndedCall: () => setCallEnded(null),
      onEvent(listener) {
        listenersRef.current.add(listener);
        return () => listenersRef.current.delete(listener);
      }
    }),
    [user, numbers, loading, revision, liveCall, callEnded, afterSession, refreshNumbers]
  );

  return <AppContext.Provider value={state}>{children}</AppContext.Provider>;
}
